var _effectorFileName = "/builds/gmini/front-ui-kit/src/FormulaInput/FormulaInput.tsx";
import { ClickAwayListener } from '@mui/material';
import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { Editor, Transforms, Range, createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { Slate, ReactEditor, withReact } from 'slate-react';
import { CrossCircle } from '../icons';
import { Element } from './Element';
import { insertMention, insertOperator } from './insertMention';
import { parseFormulaToSlate, serializeSlateToFormula } from './parse';
import { withMentions } from './withMentions';
import { HighlightOffWrapper, Editable, Backdrop, Content } from './FormulaInput.styled';
import SuggestionList from './SuggestionList';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const FormulaInput = /*#__PURE__*/React.memo(({
  allSuggestions,
  operators,
  sourceStr,
  onChange,
  onBlur,
  onFocus,
  disabled,
  suggestionsInFormula = [],
  error,
  onClear,
  focused,
  readonly
}) => {
  const containerRef = useRef(null);
  const [value, setValue] = useState([{
    children: parseFormulaToSlate(sourceStr, suggestionsInFormula, allSuggestions)
  }]);
  const existMention = useMemo(() => {
    var _value$0$children;

    return (_value$0$children = value[0].children) === null || _value$0$children === void 0 ? void 0 : _value$0$children.some(node => node.type === 'mention');
  }, [value]);
  const filledFormula = useMemo(() => {
    var _value$0$children2;

    return (_value$0$children2 = value[0].children) === null || _value$0$children2 === void 0 ? void 0 : _value$0$children2.some(children => {
      var _children$text;

      return children.type === 'mention' || Number(children === null || children === void 0 ? void 0 : (_children$text = children.text) === null || _children$text === void 0 ? void 0 : _children$text.length) > 0;
    });
  }, [value]);
  const selectedProperties = useMemo(() => {
    var _value$0$children3;

    return (_value$0$children3 = value[0].children) === null || _value$0$children3 === void 0 ? void 0 : _value$0$children3.filter(({
      type
    }) => type === 'mention');
  }, [value]);
  const [target, setTarget] = useState(null);
  const [focusedSuggestionIdx, setFocusedSuggestionIdx] = useState(0);
  const [search, setSearch] = useState('');
  const editor = useMemo(() => withMentions(withReact(withHistory(createEditor()))), []);
  const onDeleteMention = useCallback(event => {
    event.stopPropagation(); // Подробное объяснение - https://github.com/ianstormtaylor/slate/issues/4240#issuecomment-920834086

    Transforms.delete(editor);
  }, [editor]);
  const renderElement = useCallback(props => /*#__PURE__*/_jsx(Element, { ...props,
    onDelete: onDeleteMention,
    readonly: readonly
  }), [onDeleteMention, readonly]);
  const onClearHandler = useCallback(event => {
    event.stopPropagation(); // Подробное объяснение - https://github.com/ianstormtaylor/slate/issues/4240#issuecomment-920834086

    if (disabled || readonly) {
      return;
    }

    Transforms.select(editor, {
      anchor: Editor.start(editor, []),
      focus: Editor.end(editor, [])
    });
    Transforms.delete(editor);
    onClear === null || onClear === void 0 ? void 0 : onClear();
  }, [disabled, editor, onClear, readonly]);
  const suggestions = React.useMemo(() => {
    const opers = operators.filter(({
      name
    }) => name.toLowerCase().includes(search.toLowerCase())).map(item => ({
      type: 'operator',
      ...item
    }));
    const mentions = allSuggestions.filter(({
      name,
      groupName
    }) => {
      const s = search.toLowerCase();
      return name.toLowerCase().includes(s) || groupName.toLocaleLowerCase().includes(s);
    }).map(item => ({
      type: 'mention',
      ...item
    }));
    return [...opers, ...mentions];
  }, [allSuggestions, operators, search]);
  const onInsertMention = useCallback(idx => {
    Transforms.select(editor, target);
    const item = suggestions[idx];

    if (item.type === 'mention') {
      insertMention(editor, item);
    } else {
      insertOperator(editor, item);
    }

    ReactEditor.focus(editor);
    setTarget(null);
  }, [editor, suggestions, target]);
  const onKeyDown = useCallback(event => {
    if (target) {
      switch (event.key) {
        case 'ArrowDown':
          {
            event.preventDefault();
            const prevIndex = focusedSuggestionIdx >= suggestions.length - 1 ? 0 : focusedSuggestionIdx + 1;
            setFocusedSuggestionIdx(prevIndex);
            break;
          }

        case 'ArrowUp':
          {
            event.preventDefault();
            const nextIndex = focusedSuggestionIdx <= 0 ? suggestions.length - 1 : focusedSuggestionIdx - 1;
            setFocusedSuggestionIdx(nextIndex);
            break;
          }

        case 'Tab':
        case 'Enter':
          if (suggestions[focusedSuggestionIdx]) {
            event.preventDefault();
            onInsertMention(focusedSuggestionIdx);
          }

          break;

        case 'Escape':
          event.preventDefault();
          setTarget(null);
          break;
      }
    }
  }, [focusedSuggestionIdx, onInsertMention, suggestions, target]);
  const onClickSuggestion = useCallback(index => {
    onInsertMention(index);
  }, [onInsertMention]);
  const onMouseEnterSuggestion = useCallback(index => setFocusedSuggestionIdx(index), []);
  const onPopoverClickAway = useCallback(() => {
    setTarget(null);
  }, []);
  useEffect(() => {
    // сбрасываем select для избавления от рассинхронизации состояния с выборкой
    Transforms.deselect(editor); // если идет сохранение

    if (disabled || readonly) {
      return;
    } // синхронизируем состояние с пропсами


    setValue([{
      children: parseFormulaToSlate(sourceStr, suggestionsInFormula, allSuggestions)
    }]);
  }, [allSuggestions, disabled, editor, readonly, sourceStr, suggestionsInFormula]);
  return /*#__PURE__*/_jsx(ClickAwayListener, {
    onClickAway: onPopoverClickAway,
    children: /*#__PURE__*/_jsxs(Content, {
      ref: containerRef,
      children: [disabled && /*#__PURE__*/_jsx(Backdrop, {}), /*#__PURE__*/_jsxs(Slate, {
        editor: editor,
        value: value,
        onChange: value => {
          setValue(value);
          const {
            hasSpecialChar,
            result
          } = serializeSlateToFormula(value);
          onChange({
            hasSpecialChar,
            nextStr: result
          });
          const {
            selection
          } = editor;

          if (selection && Range.isCollapsed(selection)) {
            const [start] = Range.edges(selection);
            const before = Editor.before(editor, start, {
              unit: 'word'
            });
            const beforeRange = before && Editor.range(editor, before, start);
            const beforeText = beforeRange && Editor.string(editor, beforeRange);

            if (beforeText && beforeText.trim() && /^[^\d]/.test(beforeText.trim())) {
              const br = beforeRange;
              setTarget(br);
              setSearch(beforeText);
              setFocusedSuggestionIdx(0);
            } else {
              setTarget(null);
            }
          }
        },
        children: [/*#__PURE__*/_jsx(Editable, {
          renderElement: renderElement,
          onKeyDown: onKeyDown,
          onFocus: onFocus,
          onBlur: onBlur,
          placeholder: "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0444\u043E\u0440\u043C\u0443\u043B\u0443...",
          existMention: existMention,
          error: error,
          disabled: readonly
        }), target && suggestions.length > 0 && /*#__PURE__*/_jsx(SuggestionList, {
          suggestions: suggestions,
          selectedSuggestions: selectedProperties,
          focusedSuggestionIdx: focusedSuggestionIdx,
          onClickSuggestion: onClickSuggestion,
          onMouseEnterSuggestion: onMouseEnterSuggestion,
          containerRef: containerRef,
          search: search
        })]
      }), filledFormula && !readonly && /*#__PURE__*/_jsx(HighlightOffWrapper, {
        disabled: disabled,
        onClick: onClearHandler,
        "data-test-id": "clearField",
        children: /*#__PURE__*/_jsx(CrossCircle, {})
      })]
    })
  });
});
FormulaInput.displayName = 'FormulaInput';